import React from 'react';
import { Mail, Users, Brain, Rocket, Heart, Globe, Code, TrendingUp, Target, Coffee } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';

const Careers = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Careers - Join Our Team | AI Wealth Manager"
        description="Join our team of fintech experts and AI engineers. We're actively hiring passionate professionals to help democratize wealth management through artificial intelligence."
        keywords="careers, jobs, fintech jobs, AI engineer jobs, wealth management careers, financial technology careers"
      />
      
      <Navbar />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="px-4 py-16">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] bg-clip-text text-transparent">
              Join Our Team
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed mb-8">
              We're glad that you want to work with us! We're actively hiring and are always looking for great people to work with.
            </p>
            <div className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] text-black font-semibold text-lg hover:scale-105 transition-transform duration-300">
              <Mail className="h-5 w-5 mr-2" />
              Reach out to us at hr@wealthmanager.online
            </div>
          </div>
        </section>

        {/* Why Work With Us */}
        <section className="px-4 py-16 bg-black/50">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Why Work With Us?</h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Join a team that's revolutionizing wealth management through AI and making financial advice accessible to everyone.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Rocket className="h-12 w-12 text-[#9E7C47] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Innovation at Scale</h3>
                <p className="text-white/70">
                  Work on cutting-edge AI technology that's transforming how people manage their wealth and investments.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Users className="h-12 w-12 text-[#F9F1A9] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Talented Team</h3>
                <p className="text-white/70">
                  Collaborate with experienced professionals from top fintech companies and leading business schools.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Target className="h-12 w-12 text-[#9E7C47] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Meaningful Mission</h3>
                <p className="text-white/70">
                  Help democratize wealth management and make sophisticated financial tools accessible to everyone.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <TrendingUp className="h-12 w-12 text-[#F9F1A9] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Growth Opportunities</h3>
                <p className="text-white/70">
                  Fast-growing startup environment with opportunities to learn, grow, and take on new challenges.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Globe className="h-12 w-12 text-[#9E7C47] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Global Impact</h3>
                <p className="text-white/70">
                  Work on products that serve users worldwide, with technology partnerships across the globe.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <Heart className="h-12 w-12 text-[#F9F1A9] mb-4" />
                <h3 className="text-xl font-semibold mb-3">Great Culture</h3>
                <p className="text-white/70">
                  Join a team that values transparency, innovation, and collaboration in everything we do.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What We're Looking For */}
        <section className="px-4 py-16">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">What We're Looking For</h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                We're always on the lookout for talented individuals who share our passion for innovation and financial technology.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="glass-card rounded-xl p-8 bg-gradient-to-br from-[#9E7C47]/10 to-[#F9F1A9]/10 border border-[#9E7C47]/20">
                <div className="flex items-center mb-6">
                  <Code className="h-10 w-10 text-[#9E7C47] mr-4" />
                  <h3 className="text-2xl font-bold">Technical Roles</h3>
                </div>
                <ul className="space-y-3 text-white/80">
                  <li>• AI/ML Engineers & Data Scientists</li>
                  <li>• Full-Stack & Frontend Developers</li>
                  <li>• DevOps & Cloud Engineers</li>
                  <li>• Mobile App Developers</li>
                  <li>• Backend & API Engineers</li>
                  <li>• Quality Assurance Engineers</li>
                </ul>
              </div>
              
              <div className="glass-card rounded-xl p-8 bg-gradient-to-br from-[#F9F1A9]/10 to-[#9E7C47]/10 border border-[#F9F1A9]/20">
                <div className="flex items-center mb-6">
                  <Brain className="h-10 w-10 text-[#F9F1A9] mr-4" />
                  <h3 className="text-2xl font-bold">Business Roles</h3>
                </div>
                <ul className="space-y-3 text-white/80">
                  <li>• Financial Advisors & Wealth Managers</li>
                  <li>• Product Managers & Business Analysts</li>
                  <li>• Marketing & Growth Specialists</li>
                  <li>• Customer Success Managers</li>
                  <li>• Compliance & Risk Officers</li>
                  <li>• Operations & Strategy Leads</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="px-4 py-16 bg-black/50">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <div className="glass-card rounded-xl p-12 bg-gradient-to-br from-[#9E7C47]/10 to-[#F9F1A9]/10 border border-[#9E7C47]/20">
                <Coffee className="h-16 w-16 text-[#9E7C47] mx-auto mb-6" />
                <h2 className="text-3xl font-bold mb-6 text-gradient">Ready to Join Us?</h2>
                <p className="text-lg text-white/80 mb-8">
                  We'd love to hear from you! Whether you're a seasoned professional or just starting your career, 
                  if you're passionate about fintech and AI, we want to talk.
                </p>
                
                <div className="space-y-4">
                  <div className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] text-black font-semibold text-lg hover:scale-105 transition-transform duration-300">
                    <Mail className="h-5 w-5 mr-2" />
                    <a href="mailto:hr@wealthmanager.online" className="hover:underline">
                      hr@wealthmanager.online
                    </a>
                  </div>
                  
                  <p className="text-white/60 text-sm mt-4">
                    Please include your resume and tell us why you're interested in joining our mission to democratize wealth management.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="px-4 py-16">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Our Values</h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                These principles guide everything we do and help us build a great place to work.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-[#F9F1A9]">Innovation First</h3>
                <p className="text-white/70">
                  We embrace new technologies and creative solutions to solve complex financial challenges.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-[#F9F1A9]">Transparency</h3>
                <p className="text-white/70">
                  We believe in open communication, honest feedback, and building trust with our team and users.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-[#F9F1A9]">Continuous Learning</h3>
                <p className="text-white/70">
                  We invest in our team's growth and encourage learning from failures and successes alike.
                </p>
              </div>
              
              <div className="glass-card rounded-xl p-6 hover:border-[#9E7C47]/50 transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-[#F9F1A9]">User-Centric</h3>
                <p className="text-white/70">
                  Every decision we make is guided by what's best for our users and their financial success.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Careers;